import React, { useEffect, useRef, useState } from 'react';
import './SideDrawer.css';
import placeholderProfile from '../../assets/placeholder-profile.png';

const TRANSITION_MS = 500;

function SideDrawer({
  open,
  onClose,
  user,
  children,
  switchTarget = null,
  onSwitchIdentity = null,
  switchingIdentity = false,
  showAccountsIcon = false,
  onOpenAccountsDrawer = null,
  accountsIcon = null,
}) {
  const drawerRef = useRef(null);
  const closeTimerRef = useRef(null);
  const openTimerRef = useRef(null);

  const [shouldRender, setShouldRender] = useState(open);
  const [enterStage, setEnterStage] = useState(0);

  useEffect(() => {
    if (open) {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      if (openTimerRef.current) {
        clearTimeout(openTimerRef.current);
        openTimerRef.current = null;
      }

      setShouldRender(true);
      document.body.classList.add('no-scroll');
      setEnterStage(0);

      openTimerRef.current = setTimeout(() => {
        const el = drawerRef.current;
        if (!el) return;
        void el.offsetWidth;
        setEnterStage(1);
        openTimerRef.current = null;
      }, 0);
    } else if (shouldRender) {
      closeTimerRef.current = setTimeout(() => {
        setShouldRender(false);
        document.body.classList.remove('no-scroll');
        setEnterStage(0);
        closeTimerRef.current = null;
      }, TRANSITION_MS);
    }

    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      if (openTimerRef.current) {
        clearTimeout(openTimerRef.current);
        openTimerRef.current = null;
      }
    };
  }, [open, shouldRender]);

  useEffect(() => {
    if (!shouldRender) return;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shouldRender, onClose]);

  if (!shouldRender) return null;

  const avatar = user?.profilePic || placeholderProfile;
  const fullName = user?.name || user?.username || 'Pioneer';



  const isMerchant =
    user?.userType === 'merchant' && (user?.isMerchant || user?.user?.isMerchant);

  const isAdminIdentity = user?.isAdmin === true || !!user?.adminRole;
  const isAppIdentity = user?.isApp === true || user?.accountType === 'app';

  const roleLabel = isAdminIdentity
    ? 'Admin'
    : isAppIdentity
    ? 'App'
    : isMerchant
    ? 'Merchant'
    : 'Pioneer';

  const roleClass =
    roleLabel === 'Pioneer'
      ? 'homedrawer-pioneer-pill'
      : 'homedrawer-merchant-pill';



  const drawerStateClass = open
    ? (enterStage === 0 ? 'preopen' : 'open')
    : 'closing';

  const backdropStateClass = open ? 'visible' : 'fading';

  const switchAvatar =
    switchTarget?.profilePic ||
    switchTarget?.avatar ||
    placeholderProfile;

  const switchName =
    switchTarget?.name ||
    switchTarget?.username ||
    '';

  return (
    <div
      className={`homedrawer-backdrop ${backdropStateClass}`}
      onClick={onClose}
    >
      <aside
        ref={drawerRef}
        className={`homedrawer ${drawerStateClass}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="homedrawer-topbar">
          <div className="homedrawer-identity-pill">
            <div className="homedrawer-identity-main">
              <img src={avatar} alt={fullName} className="homedrawer-avatar" />

              <div className="homedrawer-meta">
                <div className="homedrawer-name">{fullName}</div>
                <div className="homedrawer-role-row">
                  <span className={roleClass}>{roleLabel}</span>
                </div>
              </div>
            </div>

            {(switchTarget || showAccountsIcon) ? (
              <div className="homedrawer-switch-actions">
                {switchTarget ? (
                  <button
                    type="button"
                    className={`homedrawer-switch-avatar-btn ${switchingIdentity ? 'is-loading' : ''}`}
                    onClick={onSwitchIdentity}
                    disabled={switchingIdentity}
                    title={switchName ? `Switch to ${switchName}` : 'Switch identity'}
                    aria-label={switchName ? `Switch to ${switchName}` : 'Switch identity'}
                  >
                    <img
                      src={switchAvatar}
                      alt={switchName || 'Switch identity'}
                      className="homedrawer-switch-avatar"
                    />
                  </button>
                ) : null}

                {showAccountsIcon && accountsIcon ? (
                  <button
                    type="button"
                    className="homedrawer-accounts-btn"
                    onClick={onOpenAccountsDrawer}
                    title="Open accounts drawer"
                    aria-label="Open accounts drawer"
                  >
                    <img
                      src={accountsIcon}
                      alt="Accounts"
                      className="homedrawer-accounts-icon"
                    />
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        <div className="homedrawer-content">
          {children}
        </div>
      </aside>
    </div>
  );
}

export default SideDrawer;
